import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Open dialogs, topmost last. Only the topmost one reacts to Escape / Tab. */
const stack: symbol[] = [];

/** Focusable descendants of `root` in DOM order (skips `hidden` / `aria-hidden` subtrees). */
export function focusablesIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.closest('[hidden], [aria-hidden="true"]') && el.getAttribute('tabindex') !== '-1',
  );
}

/**
 * Shared modal behaviour for Sheet, ConfirmDialog and the command palette:
 *  - initial focus on `[data-autofocus]`, else the first focusable inside `[data-dialog-body]`,
 *    else the container itself (give it `tabIndex={-1}`); re-runs when `resetKey` changes
 *  - Escape calls `onClose` (topmost dialog only)
 *  - Tab / Shift+Tab is trapped inside `ref`
 *  - focus returns to the element that was focused when the dialog mounted
 */
export function useDialog(
  ref: RefObject<HTMLElement | null>,
  { onClose, resetKey }: { onClose: () => void; resetKey?: unknown },
): void {
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    const token = Symbol('dialog');
    stack.push(token);
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    function onKeyDown(event: KeyboardEvent) {
      if (stack[stack.length - 1] !== token) return;
      const root = ref.current;
      if (!root) return;
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusablesIn(root);
      if (items.length === 0) {
        event.preventDefault();
        root.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (!root.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === root)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const index = stack.indexOf(token);
      if (index >= 0) stack.splice(index, 1);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
    // The ref object is stable; the effect must run once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const preferred = root.querySelector<HTMLElement>('[data-autofocus]');
    const body = root.querySelector<HTMLElement>('[data-dialog-body]');
    const target = preferred ?? (body ? focusablesIn(body)[0] : undefined) ?? root;
    target.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);
}
